/**
 * session.js — Chat session id persistence.
 *
 * PURPOSE:
 *   The widget owns the session_id (a plain string passed to the BE as a query
 *   param). It is persisted in localStorage, keyed per tenant, so it survives
 *   page reload, minimize, and revisit.
 *
 * WHY NOT A COOKIE:
 *   The widget is embedded cross-origin on winery sites; third-party cookies to
 *   the BE domain are unreliable (SameSite/blocking). session_id is not an auth
 *   credential — it is just the widget's conversation handle — so localStorage is
 *   the right store.
 *
 * USAGE:
 *   import { loadSession, persistSession, endSession } from "./session.js";
 */

import { getState, setState } from "./state.js";
import { clearHistory } from "./chat-history.js";

const keyFor = (tenantId) => `ws_session_${tenantId || "default"}`;

function setSessionId(id) {
  const { session } = getState();
  setState({ session: { ...session, id } });
}

// Returns the stored session_id for this tenant, or null.
export function loadSession(tenantId) {
  try {
    return localStorage.getItem(keyFor(tenantId)) || null;
  } catch {
    return null;
  }
}

// Stores session_id in state + localStorage.
export function persistSession(tenantId, sessionId) {
  setSessionId(sessionId);
  try {
    if (sessionId) localStorage.setItem(keyFor(tenantId), sessionId);
  } catch {
    /* storage blocked — session stays in memory for this page only */
  }
}

// Ends the session: clears state + removes the stored id AND the stored
// transcript for that session. Next open starts fresh. Capture the id before
// nulling it so we can target the right ws-chat-msgs key.
export function endSession(tenantId) {
  const endingId = getState().session.id;
  setSessionId(null);
  try {
    localStorage.removeItem(keyFor(tenantId));
  } catch {
    /* ignore */
  }
  clearHistory(tenantId, endingId);
}
